import React, { useState } from 'react'

import { Check } from 'lucide-react'

import { Integration } from '@/core/api/entities/integration'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useIntegrateWithIntegration } from '@/core/hooks/useIntegrations'
import { useTranslation } from '@/core/i18n/useTranslation'
import { getBuildType } from '@/core/utils/buildType'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { cn } from '@/shared/utils/helpers'

import { getIntegrationImage } from '../utils/imageImports'

interface IntegrationModalProps {
	isOpen: boolean
	onClose: () => void
	integration: Integration | null
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({
	isOpen,
	onClose,
	integration,
}) => {
	const { t } = useTranslation('common')
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
	const isMusicbrand = getBuildType() === 'musicbrand'

	// For musicbrand builds, try the _alternative translation key first so that
	// branding like "Powered by Brandtrack" is replaced with the neutral variant.
	// Falls back to the regular key for integrations that have no alternative.
	const resolveKey = (key: string | undefined) => {
		if (!key) return ''
		if (!isMusicbrand) return t(key)
		const parts = key.split('.')
		parts[parts.length - 1] = parts[parts.length - 1] + '_alternative'
		return t([parts.join('.'), key] as any)
	}

	// Get account config with using_account_id
	const config = useAccountConfig()

	// Use the mutation hook for integration
	const { mutate: integrateWithIntegration, isPending } =
		useIntegrateWithIntegration({
			config,
			options: {
				onSuccess: () => {
					// Close the modal and show success
					onClose()
					setIsSuccessModalOpen(true)
				},
				onError: (error: any) => {
					console.error('Error integrating:', error)
					// Handle error - you might want to show an error message to the user
				},
			},
		})

	if (!integration) return null

	const handleIntegrate = () => {
		integrateWithIntegration({
			integration_name: t(integration.name || ''),
			integration_type: integration.type,
		})
	}

	const handleCloseSuccessModal = () => {
		setIsSuccessModalOpen(false)
	}

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-md w-full max-h-[90vh] overflow-y-auto p-0 border border-border rounded-lg">
				<div className="p-6 space-y-6">
					<div className="flex">
						<div className="bg-black w-[195px] h-[126px] rounded-lg flex items-center justify-center border border-border">
							<img
								src={getIntegrationImage(integration.icon || '')}
								alt={t(integration.name)}
								className={cn(
									integration.type === 'integrations'
										? 'w-[195px] h-[126px] object-contain'
										: 'w-full h-full object-cover'
								)}
							/>
						</div>
					</div>

					<DialogHeader className="p-0">
						<DialogTitle className="text-xl font-bold text-foreground leading-tight">
							{resolveKey(integration.modal_title || integration.name)}
						</DialogTitle>
					</DialogHeader>

					<div
						className="text-secondary-foreground text-sm leading-relaxed break-words overflow-wrap-anywhere [&>p]:mb-4 [&>p]:last:mb-0 [&>a]:text-blue-400 [&>a]:underline [&>a]:hover:text-blue-300"
						dangerouslySetInnerHTML={{
							__html: t(integration.modal_content || ''),
						}}
					/>

					{/* Action Buttons */}
					<div className="flex justify-end space-x-3 pt-4">
						<Button
							variant="outline"
							onClick={onClose}
							className="px-6 rounded-full"
						>
							{t('integrations.modal.button_cancel')}
						</Button>
						<Button
							variant="default"
							onClick={handleIntegrate}
							disabled={isPending}
							className="px-6 rounded-full"
						>
							{isPending
								? t('integrations.modal.submitting')
								: t('integrations.modal.button_submit')}
						</Button>
					</div>
				</div>
			</DialogContent>

			{/* Success Modal */}
			<Dialog open={isSuccessModalOpen} onOpenChange={handleCloseSuccessModal}>
				<DialogContent className="max-w-sm w-[500px] h-[400px] p-0 bg-background border border-border rounded-lg">
					<div className="flex flex-col p-6 space-y-5">
						<div className="flex">
							<div className="w-8 h-8 bg-shade rounded-lg flex items-center justify-center">
								<div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
									<Check className="w-4 h-4 text-black" />
								</div>
							</div>
						</div>

						<DialogTitle className="flex-1 text-foreground text-2xl font-medium">
							{t('integrations.modal.success.title')}
						</DialogTitle>

						<div className="flex justify-end pt-5 border-t border-border">
							<Button
								variant="default"
								onClick={handleCloseSuccessModal}
								className="px-6 rounded-full"
							>
								{t('integrations.modal.success.button')}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</Dialog>
	)
}

export default IntegrationModal
